import { useState, type JSX } from 'react';
import {
  calendarAppointmentKind,
  calendarWorkingHours,
  generateUuidV7,
  eligibleAdvisersSchema,
  adviserDetailSchema,
  type ConfigurationDraft,
  type BotScope,
} from '@cryptology-assist/domain';
import { botPath, calendarsSchema, type Snapshot } from './api';
import { useRemote } from './hooks';
import { ErrorNotice, Field } from './components';
import type { Copy } from './copy';

type Service = NonNullable<ConfigurationDraft['appointmentServices']>[number];
type Calendar = Service['calendar'];
type PoolCalendar = Extract<Calendar, { pool: object }>;

function AdviserChoice({
  scope,
  member,
  copy,
  onChange,
  remove,
}: {
  scope: BotScope;
  member: PoolCalendar['pool']['members'][number];
  copy: Copy;
  onChange: (id: string) => void;
  remove: () => void;
}) {
  const detail = useRemote(
    botPath(scope, `advisers/${member.adviserId}`),
    adviserDetailSchema
  );
  return (
    <div className="service-adviser-row">
      <div>
        <strong>{detail.data?.adviser.name ?? copy.loading}</strong>
        <ErrorNotice error={detail.error} copy={copy} onRetry={detail.reload} />
        {detail.data && (
          <Field label={copy.bookingCalendar}>
            <select
              value={member.bookingCalendarId}
              onChange={(event) => onChange(event.target.value)}
            >
              <option value="">{copy.selectCalendar}</option>
              {detail.data.calendars
                .filter((calendar) => calendar.writable)
                .map((calendar) => (
                  <option key={calendar.id} value={calendar.id}>
                    {calendar.name} · {calendar.address}
                  </option>
                ))}
            </select>
          </Field>
        )}
      </div>
      <button type="button" onClick={remove}>
        {copy.remove}
      </button>
    </div>
  );
}
function PoolEditor({
  scope,
  calendar,
  copy,
  update,
}: {
  scope: BotScope;
  calendar: PoolCalendar;
  copy: Copy;
  update: (calendar: PoolCalendar) => void;
}) {
  const [after, setAfter] = useState<string | null>(null);
  const directory = useRemote(
    botPath(scope, `advisers${after ? `?after=${after}` : ''}`),
    eligibleAdvisersSchema
  );
  const [selected, setSelected] = useState('');
  const pool = calendar.pool;
  return (
    <section className="service-team">
      <h3>{copy.serviceAdvisers}</h3>
      <p>{copy.adviserAssignmentHelp}</p>
      {pool.members.map((member) => (
        <AdviserChoice
          key={member.adviserId}
          {...{ scope, member, copy }}
          onChange={(id) =>
            update({
              ...calendar,
              pool: {
                ...pool,
                members: pool.members.map((value) =>
                  value.adviserId === member.adviserId
                    ? { ...value, bookingCalendarId: id }
                    : value
                ),
              },
            })
          }
          remove={() =>
            update({
              ...calendar,
              pool: {
                ...pool,
                members: pool.members.filter(
                  (value) => value.adviserId !== member.adviserId
                ),
              },
            })
          }
        />
      ))}
      <ErrorNotice
        error={directory.error}
        copy={copy}
        onRetry={directory.reload}
      />
      {directory.data?.advisers.length === 0 && (
        <p className="notice-inline">{copy.noEligibleAdvisers}</p>
      )}
      <div className="inline-fields">
        <Field label={copy.addAdviser}>
          <select
            value={selected}
            onChange={(event) => setSelected(event.target.value)}
          >
            <option value="">
              {directory.loading ? copy.loading : copy.selectAdviser}
            </option>
            {directory.data?.advisers
              .filter(
                (value) =>
                  !pool.members.some(
                    (member) => member.adviserId === value.adviser.id
                  )
              )
              .map((value) => (
                <option key={value.adviser.id} value={value.adviser.id}>
                  {value.adviser.name}
                </option>
              ))}
          </select>
        </Field>
        <button
          type="button"
          disabled={!selected || pool.members.length >= 50}
          onClick={() => {
            update({
              ...calendar,
              pool: {
                ...pool,
                members: [
                  ...pool.members,
                  { adviserId: selected, bookingCalendarId: '' },
                ],
              },
            });
            setSelected('');
          }}
        >
          {copy.addAdviser}
        </button>
      </div>
      <div className="form-actions">
        {after && (
          <button
            type="button"
            onClick={() => {
              setAfter(null);
              setSelected('');
            }}
          >
            {copy.firstPage}
          </button>
        )}
        {directory.data?.nextCursor && (
          <button
            type="button"
            onClick={() => {
              setAfter(directory.data!.nextCursor);
              setSelected('');
            }}
          >
            {copy.nextPage}
          </button>
        )}
      </div>
      <details className="advanced-settings">
        <summary>{copy.bookingRules}</summary>
        <p>{copy.bookingRulesHelp}</p>
        <div className="form-grid">
          {(
            [
              'noticeMinutes',
              'bufferBeforeMinutes',
              'bufferAfterMinutes',
              'horizonDays',
            ] as const
          ).map((key) => (
            <Field key={key} label={copy[key]}>
              <input
                type="number"
                min={key === 'horizonDays' ? 1 : 0}
                max={
                  key === 'horizonDays'
                    ? 90
                    : key === 'noticeMinutes'
                      ? 43200
                      : 240
                }
                value={pool[key]}
                onChange={(event) =>
                  update({
                    ...calendar,
                    pool: { ...pool, [key]: Number(event.target.value) },
                  })
                }
              />
            </Field>
          ))}
        </div>
      </details>
    </section>
  );
}
function LegacyCalendar({
  scope,
  calendar,
  connections,
  copy,
  update,
}: {
  scope: BotScope;
  calendar: Exclude<Calendar, { pool: object }>;
  connections: Snapshot['connections'];
  copy: Copy;
  update: (calendar: Calendar) => void;
}) {
  const choices = useRemote(
    calendar.connectionId
      ? botPath(scope, `connections/${calendar.connectionId}/calendars`)
      : null,
    calendarsSchema
  );
  return (
    <section className="service-team">
      <h3>{copy.calendarAccount}</h3>
      <p>{copy.directCalendarHelp}</p>
      <div className="form-grid">
        <Field label={copy.calendarAccount}>
          <select
            value={calendar.connectionId}
            onChange={(event) =>
              update({
                ...calendar,
                connectionId: event.target.value,
                calendarId: '',
              })
            }
          >
            <option value="">{copy.selectCalendarAccount}</option>
            {connections
              .filter(
                (value) => value.channel === 'email' && value.status === 'ready'
              )
              .map((value) => (
                <option key={value.id} value={value.id}>
                  {value.address} · {value.provider}
                </option>
              ))}
          </select>
        </Field>
        <Field label={copy.calendarChoice}>
          <select
            value={calendar.calendarId}
            onChange={(event) =>
              update({ ...calendar, calendarId: event.target.value })
            }
          >
            <option value="">
              {choices.loading ? copy.loading : copy.selectCalendar}
            </option>
            {choices.data?.calendars
              .filter((value) => value.writable)
              .map((value) => (
                <option key={value.id} value={value.id}>
                  {value.name}
                </option>
              ))}
          </select>
        </Field>
      </div>
      <ErrorNotice error={choices.error} copy={copy} onRetry={choices.reload} />
      <button
        type="button"
        onClick={() => {
          update({
            timezone: calendar.timezone,
            durationMinutes: calendar.durationMinutes,
            appointmentKind: calendar.appointmentKind,
            serviceName: calendar.serviceName,
            location: calendar.location,
            pool: {
              id: generateUuidV7(),
              members: [],
              noticeMinutes: 60,
              bufferBeforeMinutes: 0,
              bufferAfterMinutes: 0,
              horizonDays: 30,
            },
            weeklyHours: calendarWorkingHours(calendar),
          });
        }}
      >
        {copy.useAdviserTeam}
      </button>
    </section>
  );
}
function ServiceEditor({
  scope,
  service,
  connections,
  copy,
  locale,
  update,
  remove,
}: {
  scope: BotScope;
  service: Service;
  connections: Snapshot['connections'];
  copy: Copy;
  locale: string;
  update: (service: Service) => void;
  remove: () => void;
}): JSX.Element {
  const calendar = service.calendar;
  const hours = calendarWorkingHours(calendar);
  const days = new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    timeZone: 'UTC',
  });
  function changeCalendar(value: Calendar) {
    update({ ...service, calendar: value });
  }
  return (
    <section className="service-editor form-section">
      <div className="section-toolbar">
        <h3>{service.name || copy.newService}</h3>
        <button type="button" onClick={remove}>
          {copy.remove}
        </button>
      </div>
      <div className="form-grid">
        <Field label={copy.serviceName}>
          <input
            maxLength={120}
            value={service.name}
            onChange={(event) =>
              update({ ...service, name: event.target.value })
            }
          />
        </Field>
        <Field label={copy.appointmentKind}>
          <select
            value={calendarAppointmentKind(calendar)}
            onChange={(event) =>
              changeCalendar({
                ...calendar,
                appointmentKind: event.target.value as
                  'sales_call' | 'site_visit' | 'appointment',
              })
            }
          >
            <option value="sales_call">{copy.salesCall}</option>
            <option value="site_visit">{copy.siteVisit}</option>
            <option value="appointment">{copy.serviceAppointment}</option>
          </select>
        </Field>
        {calendarAppointmentKind(calendar) !== 'sales_call' && (
          <Field label={copy.serviceLocation}>
            <input
              maxLength={500}
              value={service.location}
              onChange={(event) =>
                update({ ...service, location: event.target.value })
              }
            />
          </Field>
        )}
        <Field label={copy.duration}>
          <input
            type="number"
            min={5}
            max={240}
            value={calendar.durationMinutes || ''}
            onChange={(event) =>
              changeCalendar({
                ...calendar,
                durationMinutes: Number(event.target.value),
              })
            }
          />
        </Field>
        <Field label={copy.timezone}>
          <select
            value={calendar.timezone}
            onChange={(event) =>
              changeCalendar({ ...calendar, timezone: event.target.value })
            }
          >
            {[
              ...new Set([
                calendar.timezone,
                ...Intl.supportedValuesOf('timeZone'),
              ]),
            ].map((timezone) => (
              <option key={timezone}>{timezone}</option>
            ))}
          </select>
        </Field>
      </div>
      {'pool' in calendar ? (
        <PoolEditor {...{ scope, calendar, copy }} update={changeCalendar} />
      ) : (
        <LegacyCalendar
          {...{ scope, calendar, connections, copy }}
          update={changeCalendar}
        />
      )}
      <section className="form-section">
        <h3>{copy.serviceWorkingHours}</h3>
        <p className="small muted">{copy.serviceHoursHelp}</p>
        {hours.map((entry, index) => (
          <div className="working-window" key={index}>
            <Field label={copy.weekday}>
              <select
                value={entry.day}
                onChange={(event) =>
                  changeCalendar({
                    ...calendar,
                    weeklyHours: hours.map((value, i) =>
                      i === index
                        ? { ...value, day: Number(event.target.value) }
                        : value
                    ),
                  })
                }
              >
                {Array.from({ length: 7 }, (_, day) => (
                  <option key={day} value={day}>
                    {days.format(new Date(Date.UTC(2026, 0, 4 + day)))}
                  </option>
                ))}
              </select>
            </Field>
            {(['opensAt', 'closesAt'] as const).map((key) => (
              <Field key={key} label={copy[key]}>
                <input
                  type="time"
                  value={entry[key]}
                  onChange={(event) =>
                    changeCalendar({
                      ...calendar,
                      weeklyHours: hours.map((value, i) =>
                        i === index
                          ? { ...value, [key]: event.target.value }
                          : value
                      ),
                    })
                  }
                />
              </Field>
            ))}
            <button
              type="button"
              onClick={() =>
                changeCalendar({
                  ...calendar,
                  weeklyHours: hours.filter((_, i) => i !== index),
                })
              }
            >
              {copy.remove}
            </button>
          </div>
        ))}
        <button
          type="button"
          disabled={hours.length >= 21}
          onClick={() =>
            changeCalendar({
              ...calendar,
              weeklyHours: [...hours, { day: 1, opensAt: '', closesAt: '' }],
            })
          }
        >
          {copy.addWorkingWindow}
        </button>
      </section>
    </section>
  );
}
export function AppointmentServices({
  scope,
  value,
  onChange,
  connections,
  copy,
  locale,
}: {
  scope: BotScope;
  value: Service[];
  onChange: (services: Service[]) => void;
  connections: Snapshot['connections'];
  copy: Copy;
  locale: string;
}): JSX.Element {
  return (
    <section className="form-section">
      <h2>{copy.appointmentServices}</h2>
      <p>{copy.appointmentServicesHelp}</p>
      {value.map((service) => (
        <ServiceEditor
          key={service.id}
          {...{ scope, service, connections, copy, locale }}
          update={(updated) =>
            onChange(
              value.map((item) => (item.id === service.id ? updated : item))
            )
          }
          remove={() =>
            onChange(value.filter((item) => item.id !== service.id))
          }
        />
      ))}
      <button
        type="button"
        disabled={value.length >= 20}
        onClick={() =>
          onChange([
            ...value,
            {
              id: generateUuidV7(),
              name: '',
              location: '',
              calendar: {
                pool: {
                  id: generateUuidV7(),
                  members: [],
                  noticeMinutes: 60,
                  bufferBeforeMinutes: 0,
                  bufferAfterMinutes: 0,
                  horizonDays: 30,
                },
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                durationMinutes: 30,
                weeklyHours: [],
                appointmentKind: 'appointment',
              },
            },
          ])
        }
      >
        {copy.addAppointmentService}
      </button>
    </section>
  );
}
